import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ContentProvider } from "@/context/ContentContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Leaf } from "lucide-react";
import Home from "@/pages/Home";
import WellnessDiscovery from "@/pages/WellnessDiscovery";
import TerrainAndTruth from "@/pages/TerrainAndTruth";
import ArticlePost from "@/pages/ArticlePost";
import DetoxGospel from "@/pages/DetoxGospel";
import Favorites from "@/pages/Favorites";
import Register from "@/pages/Register";
import Login from "@/pages/Login";
import MyWellnessJourney from "@/pages/MyWellnessJourney";
import Assessment from "@/pages/Assessment";
import Results from "@/pages/Results";
import AuthCallback from "@/pages/AuthCallback";
import AcceptInvite from "@/pages/AcceptInvite";
import ResetPassword from "@/pages/ResetPassword";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminMessaging from "@/pages/admin/AdminMessaging";
import AdminTerrain from "@/pages/admin/AdminTerrain";
import AdminEbook from "@/pages/admin/AdminEbook";
import AdminFavorites from "@/pages/admin/AdminFavorites";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminMemberDetail from "@/pages/admin/AdminMemberDetail";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminAssessment from "@/pages/admin/AdminAssessment";
import AdminConsultations from "@/pages/admin/AdminConsultations";
import AdminContent from "@/pages/admin/AdminContent";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminAudit from "@/pages/admin/AdminAudit";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const LoadingScreen = () => (
  <div className="flex min-h-screen items-center justify-center bg-ivory">
    <Leaf className="h-8 w-8 animate-pulse text-forest" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { member } = useAuth();
  const location = useLocation();
  if (member === null) return <LoadingScreen />;
  if (member === false) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
};

const AppRoutes = () => {
  const location = useLocation();
  // Detect the OAuth session_id synchronously during render, before protected routes run.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/wellness-discovery" element={<WellnessDiscovery />} />
      <Route path="/terrain-and-truth" element={<TerrainAndTruth />} />
      <Route path="/terrain-and-truth/post/:itemId" element={<ArticlePost />} />
      <Route path="/the-detox-gospel" element={<DetoxGospel />} />
      <Route path="/favorites" element={<Favorites />} />
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route
        path="/my-wellness-journey"
        element={
          <ProtectedRoute>
            <MyWellnessJourney />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessment"
        element={
          <ProtectedRoute>
            <Assessment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/results/:assessmentId"
        element={
          <ProtectedRoute>
            <Results />
          </ProtectedRoute>
        }
      />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminOverview />} />
        <Route path="team" element={<AdminTeam />} />
        <Route path="members" element={<AdminMembers />} />
        <Route path="members/:userId" element={<AdminMemberDetail />} />
        <Route path="contacts" element={<AdminContacts />} />
        <Route path="assessment" element={<AdminAssessment />} />
        <Route path="consultations" element={<AdminConsultations />} />
        <Route path="messaging" element={<AdminMessaging />} />
        <Route path="terrain" element={<AdminTerrain />} />
        <Route path="ebook" element={<AdminEbook />} />
        <Route path="favorites" element={<AdminFavorites />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="integrations" element={<AdminIntegrations />} />
        <Route path="audit" element={<AdminAudit />} />
      </Route>
    </Routes>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <ContentProvider>
        <ScrollToTop />
        <div className="App bg-ivory">
          <div className="grain-overlay" aria-hidden="true" />
          <Header />
          <AppRoutes />
          <Footer />
          <Toaster
            position="bottom-center"
            toastOptions={{
              style: {
                background: "#173F35",
                color: "#FAF8F2",
                border: "1px solid rgba(180,146,83,0.4)",
              },
            }}
          />
        </div>
        </ContentProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
